// AI tool-calling / agent-loop infrastruktur. Registry över tools som
// AI-assistenten kan invoka (add-block, connect, create-layer, etc.).
// Pure; LLM-integration lever i Edge Function.

/**
 * Kända core-tools i basen-registret. Extension-layers (t.ex.
 * `aiToolWrappersExtended.ts`) lägger till fler names via `ExtendedToolName`
 * — därav typen på `ToolDefinition.name` är `string` (A-023). Unionen nedan
 * fungerar fortfarande som dokumentation för core-tools.
 */
export type ToolName =
  | 'add_block'
  | 'update_block'
  | 'delete_block'
  | 'create_connector'
  | 'create_layer'
  | 'rename_sheet'
  | 'add_sheet'
  | 'search_workflows';

export interface ToolParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description: string;
  enum?: string[];
}

export interface ToolDefinition {
  /**
   * A-023: `string` (inte `ToolName`-union) så extension-registries kan
   * deklarera nya tool-names utan `as unknown as`-casts. Runtime-lookup
   * sker via string-equality i `findTool` / `validateToolCall`, så ingen
   * type-safety går förlorad.
   */
  name: string;
  description: string;
  parameters: ToolParameter[];
  /** Side-effect-level: 'read' = safe, 'write' = modifierar state, 'destructive' = kan förlora data. */
  sideEffect: 'read' | 'write' | 'destructive';
  /** Om tiern krävs för att använda. */
  minTier?: 'free' | 'advanced' | 'pro' | 'max' | 'enterprise';
}

export const AI_TOOLS: ToolDefinition[] = [
  {
    name: 'add_block',
    description: 'Lägg till ett block på aktuellt sheet.',
    parameters: [
      { name: 'type', type: 'string', required: true, description: 'Block-typ', enum: ['general', 'condition', 'send-email', 'webhook'] },
      { name: 'name', type: 'string', required: true, description: 'Block-namn (synligt label).' },
      { name: 'x', type: 'number', required: false, description: 'X-koordinat (auto om unset).' },
      { name: 'y', type: 'number', required: false, description: 'Y-koordinat (auto om unset).' },
    ],
    sideEffect: 'write',
  },
  {
    name: 'update_block',
    description: 'Uppdatera properties på ett befintligt block.',
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Block-id.' },
      { name: 'name', type: 'string', required: false, description: 'Nytt namn.' },
      { name: 'color', type: 'string', required: false, description: 'Ny färg (hex).' },
    ],
    sideEffect: 'write',
  },
  {
    name: 'delete_block',
    description: 'Radera ett block. Tar också bort connectors som refererar.',
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Block-id.' },
    ],
    sideEffect: 'destructive',
  },
  {
    name: 'create_connector',
    description: 'Koppla ihop två block.',
    parameters: [
      { name: 'fromId', type: 'string', required: true, description: 'Käll-block-id.' },
      { name: 'toId', type: 'string', required: true, description: 'Mål-block-id.' },
      { name: 'label', type: 'string', required: false, description: 'Valfri label.' },
    ],
    sideEffect: 'write',
  },
  {
    name: 'create_layer',
    description: 'Skapa en navigerbar layer.',
    parameters: [
      { name: 'name', type: 'string', required: true, description: 'Layer-namn.' },
      { name: 'blockIds', type: 'array', required: false, description: 'Blocks att placera i layern.' },
    ],
    sideEffect: 'write',
  },
  {
    name: 'rename_sheet',
    description: 'Ändra sheet-namn.',
    parameters: [
      { name: 'sheetId', type: 'string', required: true, description: 'Sheet-id.' },
      { name: 'name', type: 'string', required: true, description: 'Nytt namn.' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'add_sheet',
    description: 'Lägg till ny sheet.',
    parameters: [
      { name: 'name', type: 'string', required: true, description: 'Sheet-namn.' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'search_workflows',
    description: 'Sök genom användarens workflows efter text.',
    parameters: [
      { name: 'query', type: 'string', required: true, description: 'Sökterm.' },
      { name: 'limit', type: 'number', required: false, description: 'Max antal träffar.' },
    ],
    sideEffect: 'read',
  },
];

export interface ToolCall {
  toolName: string;
  args: Record<string, unknown>;
  callId: string;
}

export interface ToolCallValidation {
  ok: boolean;
  errors: string[];
}

/**
 * Validera ett tool-call innan execution: finns tool:et? required params?
 * enum-values? tier-access?
 */
export function validateToolCall(
  call: ToolCall,
  userTier: 'free' | 'advanced' | 'pro' | 'max' | 'enterprise' = 'free',
): ToolCallValidation {
  const errors: string[] = [];
  const tool = AI_TOOLS.find(t => t.name === call.toolName);
  if (!tool) {
    return { ok: false, errors: [`okänt tool: ${call.toolName}`] };
  }
  const tierOrder = ['free', 'advanced', 'pro', 'max', 'enterprise'];
  if (tool.minTier && tierOrder.indexOf(userTier) < tierOrder.indexOf(tool.minTier)) {
    errors.push(`tool "${tool.name}" kräver minst ${tool.minTier}, du har ${userTier}`);
  }
  for (const p of tool.parameters) {
    const v = call.args[p.name];
    if (p.required && (v === undefined || v === null)) {
      errors.push(`required param saknas: ${p.name}`);
      continue;
    }
    if (v !== undefined && v !== null) {
      const actualType = Array.isArray(v) ? 'array' : typeof v;
      if (actualType !== p.type && !(p.type === 'object' && actualType === 'object')) {
        errors.push(`param "${p.name}": förväntade ${p.type}, fick ${actualType}`);
      }
      if (p.enum && typeof v === 'string' && !p.enum.includes(v)) {
        errors.push(`param "${p.name}": "${v}" inte i enum [${p.enum.join(', ')}]`);
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function findTool(name: string): ToolDefinition | null {
  return AI_TOOLS.find(t => t.name === name) ?? null;
}
